import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { closeModal, formatDate, openModal, showToastMessage, useDialogStore } from 'enable-ui';
import { useFormik } from 'formik';

import { MODAL_KEY } from 'modules/auth/core/modal-key';
import type { Exercises, Lessons } from 'modules/dashboard/core/type';

import {
  GamificationType,
  type LearnerMarkingAbsence,
  type LearnerResult,
  type LessonDetail,
} from '../type';

import { differenceInMinutes, isSameMinute } from 'date-fns';
import { getTimeLine } from 'helpers';
import { apiCall } from 'hooks/service';
import { flatMap, map } from 'lodash';

import { LESSON } from '../enum';

export const useLessonDetailController = () => {
  const location = useLocation();

  const navigationTo = useNavigate();

  const params = useParams();

  const { id } = params;

  const dialogStore = useDialogStore((s) => s.state.data);

  const isEditAttendance = dialogStore[MODAL_KEY.CLASS_ABSENT]?.isEditAttendance;

  const [activeKey, setActiveKey] = useState<string>(LESSON.RESULT);

  const [learnerResult, setLearnerResult] = useState<LearnerResult[]>([]);

  const [lesson, setLesson] = useState<Lessons>();

  const [exercises, setExercises] = useState<Exercises[]>([]);

  const [learnerMarkingAbsence, setLearnerMarkingAbsence] = useState<LearnerMarkingAbsence[]>([]);

  const [levels, setLevels] = useState<number[]>([1, 2, 3]);

  const [level, setLevel] = useState<number>(0);

  const [index, setIndex] = useState<number>(0);

  const classScheduleId = location.state?.classScheduleId;

  const classLessonId = location.state?.classLessonId;

  useEffect(() => {
    if (!classScheduleId) {
      getLessonDetail();

      return;
    }

    getLearnerResult();

    getExercises();
  }, [classScheduleId, id]);

  const getLearnerResult = async () => {
    const result: LessonDetail = await apiCall({
      url: `/class-schedules/${classScheduleId}/learners`,
      method: 'GET',
      isLoading: true,
    });

    if (result?.result.length) {
      setLearnerResult(result.result);

      setLearnerMarkingAbsence(
        result.result.map((ele) => ({
          userId: ele.learner.id,
          name: ele.learner.name,
          avatar: ele.learner.physicalProfile?.avatar ?? '',
          isAbsent: false,
        })),
      );
    }

    if (result?.lesson) {
      setLesson(result.lesson);
      const startDate = (result.lesson.start ? new Date(result.lesson.start) : '') as Date;

      const endDate = (result.lesson.end ? new Date(result.lesson.end) : '') as Date;

      const startTime = formatDate(startDate, 'HH:mm');

      const endTime = formatDate(endDate, 'HH:mm');

      setValues({
        start: startTime,
        end: endTime,
        date: result.lesson.start ?? '',
      });
    }
  };

  const getLessonDetail = async () => {
    const result: Lessons = await apiCall({
      url: `/lessons/${id}`,
      method: 'GET',
      isLoading: true,
    });

    if (result?.id) {
      setLesson(result);

      const allExercises: Exercises[] = flatMap([1, 2, 3], (exerciseLevel) =>
        map(
          (result as unknown as Record<string, Exercises[]>)[`level${exerciseLevel}`],
          (exercise) => ({
            ...exercise,
            level: exerciseLevel,
          }),
        ),
      );

      setExercises(allExercises);
    }
  };

  const handleAbsent = (userId: string) => {
    setLearnerMarkingAbsence((prev) => {
      const clone = [...prev];

      const indexClone = clone.findIndex((ele) => ele.userId === userId);

      clone[indexClone].isAbsent = !clone[indexClone].isAbsent;

      return clone;
    });
  };

  const handleCancel = () => {
    if (!isEditAttendance) {
      handleSubmitAbsentLearner();

      return;
    }

    closeModal(MODAL_KEY.CLASS_ABSENT);
    getLearnerResult();
  };

  // const getLearnersMarkingAbsence = async () => {
  //   const result: LearnerMarkingAbsence[] = await apiCall({
  //     url: `/gamification/${classScheduleId}`,
  //     method: 'GET',
  //   });

  //   const formatResult = result.map((ele) => ({ ...ele, isAbsent: false }));

  //   setLearnerMarkingAbsence(formatResult);
  // };

  const getExercises = async () => {
    const result: Exercises[] = await apiCall({
      url: `/class-schedules/${classScheduleId}/lessons-or-assessments`,
      method: 'GET',
      isLoading: true,
    });

    if (result.length) {
      // Sort exercises by level
      const sortedExercises = result.sort((a, b) => (a.level || 0) - (b.level || 0));

      sortedExercises.forEach((el, i) => {
        el.index = i;
      });

      setExercises(sortedExercises);
    }
  };

  const startLesson = async () => {
    const res: { id: string } = await apiCall({
      url: `/class-schedules/${classScheduleId}/start`,
      method: 'POST',
      showError: true,
      isLoading: true,
    });

    if (res.id) {
      showToastMessage('success', `The lesson has started successfully!`);
      getLearnerResult();
    }
  };

  const finishLesson = async () => {
    const res: { id: string } = await apiCall({
      url: `/class-schedules/${classScheduleId}/stop`,
      method: 'POST',
      showError: true,
      isLoading: true,
    });

    if (res.id) {
      showToastMessage('success', `The lesson has finished!`);
      // getLearnersMarkingAbsence();
      openModal(MODAL_KEY.CLASS_ABSENT);
    }
  };

  const handleSubmitAbsentLearner = async () => {
    const dataBodyLearner = learnerMarkingAbsence.map((learner) => {
      const { avatar, name, ...rest } = learner;

      return { ...rest, comment: '', type: GamificationType.LEARNER };
    });

    const data = {
      classScheduleId,
      results: dataBodyLearner,
    };

    const dataEdit = {
      classScheduleId,
      absentData: dataBodyLearner.map((el) => ({ userId: el.userId, isAbsent: el.isAbsent })),
    };

    try {
      await apiCall({
        url: isEditAttendance ? '/gamification/remark-as-absent' : `/gamification`,
        method: 'POST',
        data: isEditAttendance ? dataEdit : data,
        showError: true,
        isLoading: true,
      });
      closeModal(MODAL_KEY.CLASS_ABSENT);
      getLearnerResult();
      showToastMessage('success', `Absence marked successfully!`);
    } catch (error) {
      throw new Error(String(error));
    }
  };

  const onTabClick = (key: string) => {
    setActiveKey(key);
  };

  const formik = useFormik({
    initialValues: {
      start: '',
      end: '',
      date: '',
    },

    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      let result: any;

      if (classScheduleId) {
        result = await apiCall({
          url: `/class-schedules/${classScheduleId}`,
          method: 'PATCH',
          data: {
            start: getTimeLine(new Date(values.date), values.start).toISOString(),
            end: getTimeLine(new Date(values.date), values.end).toISOString(),
          },
          isLoading: true,
          showError: true,
        });
      } else {
        result = await apiCall({
          url: `/class-schedules`,
          method: 'POST',
          data: {
            data: [
              {
                classLessonId,
                start: getTimeLine(new Date(values.date), values.start).toISOString(),
                end: getTimeLine(new Date(values.date), values.end).toISOString(),
              },
            ],
          },
          isLoading: true,
          showError: true,
        });
      }

      if (result?.id || result?.length) {
        showToastMessage('success', 'Updated schedule successfully!');
      }
    },
  });

  const { values, handleSubmit, handleChange, isSubmitting, isValid, setValues, setFieldValue } =
    formik;

  const handleChangeDate = (value: Date) => {
    setFieldValue('date', value);
  };

  const disabled =
    !isValid ||
    isSubmitting ||
    differenceInMinutes(lesson?.start ?? '', lesson?.end ?? '') >= 0 ||
    (isSameMinute(lesson?.start ?? '', getTimeLine(new Date(values.date), values.start)) &&
      isSameMinute(lesson?.end ?? '', getTimeLine(new Date(values.date), values.end))) ||
    values.start > values.end;

  const goBack = () => {
    navigationTo(-1);
  };

  const dataFilterByLevel = useMemo(() => {
    return exercises.filter((ex) => ex?.level === level);
  }, [exercises, index, level]);

  const handleCloseModal = () => {
    setIndex(0);
    setLevel(0);
    closeModal(MODAL_KEY.SHOW_VIDEO);
  };

  const editAttendance = () => {
    openModal(MODAL_KEY.CLASS_ABSENT, { isEditAttendance: true });
  };

  return {
    activeKey,
    onTabClick,
    learnerResult,
    exercises,
    lesson,
    values,
    handleSubmit,
    handleChange,
    disabled,
    handleChangeDate,
    goBack,
    levels,
    setLevel,
    setIndex,
    index,
    dataFilterByLevel,
    handleCloseModal,
    startLesson,
    finishLesson,
    learnerMarkingAbsence,
    handleAbsent,
    handleSubmitAbsentLearner,
    handleCancel,
    editAttendance,
  };
};
